<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Calendario de Garantías Pendientes</h1>
    </div>

    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="mb-6">
        <h2 class="text-xl font-semibold mb-2">Vista Anual {{ anioSeleccionado }}</h2>
        <p class="text-gray-600">
          Visualización mensual del número de garantías con fecha de cierre pendiente.
          Haz clic en un mes para ver el detalle.
        </p>
      </div>

      <div class="flex justify-center mb-8">
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button 
            v-for="anio in anios" 
            :key="anio"
            @click="anioSeleccionado = anio"
            :class="[
              'px-4 py-2 text-sm font-medium',
              anio === anioSeleccionado 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200',
              anio === anios[0] ? 'rounded-l-lg' : '',
              anio === anios[anios.length - 1] ? 'rounded-r-lg' : 'border-l-0',
            ]"
          >
            {{ anio }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-10">
        <p>Cargando calendario...</p>
      </div>
      <div v-if="error" class="text-center py-10 text-red-500">
        <p>{{ error }}</p>
      </div>

      <div v-if="!loading && !error" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="mes in meses" 
          :key="mes.value"
          @click="verMes(mes.value)"
          class="border rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow"
        >
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-lg font-semibold">{{ mes.nombre }}</h3>
            <span 
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="{
                'bg-green-100 text-green-800': getGarantiasPorMes(mes.value) === 0,
                'bg-yellow-100 text-yellow-800': getGarantiasPorMes(mes.value) > 0 && getGarantiasPorMes(mes.value) < 5,
                'bg-red-100 text-red-800': getGarantiasPorMes(mes.value) >= 5
              }"
            >
              {{ getGarantiasPorMes(mes.value) }} garantía(s)
            </span>
          </div>
          
          <div class="h-40 bg-gray-50 rounded p-2">
            <div class="grid grid-cols-7 gap-1 text-xs text-center">
              <div v-for="dia in ['D', 'L', 'M', 'M', 'J', 'V', 'S']" :key="dia" class="font-semibold">{{ dia }}</div>
              <div 
                v-for="(dia, index) in getDiasMes(mes.value, anioSeleccionado)" 
                :key="index"
                class="p-1 rounded-full"
                :class="{
                  'bg-red-100': dia.garantias > 0,
                  'text-red-800 font-semibold': dia.garantias > 0
                }"
                :title="dia.garantias > 0 ? `${dia.garantias} garantía(s) el ${dia.dia}` : ''"
              >
                {{ dia.dia }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="mesSeleccionado" class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold">
          Garantías de {{ getNombreMes(mesSeleccionado) }} {{ anioSeleccionado }}
          <span class="text-sm font-normal text-gray-600 ml-2">({{ garantiasFiltradas.length }} garantías)</span>
        </h2>
        <button @click="mesSeleccionado = null" class="text-gray-500 hover:text-gray-700">
          <i class="la la-times text-xl"></i>
        </button>
      </div>

      <div v-if="garantiasFiltradas.length > 0" class="overflow-x-auto">
        <table class="min-w-full bg-white">
          <thead>
            <tr class="bg-gray-100">
              <th class="py-3 px-4 text-left">Folio</th>
              <th class="py-3 px-4 text-left">Cliente</th>
              <th class="py-3 px-4 text-left">Producto</th>
              <th class="py-3 px-4 text-left">Fecha Cierre</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="garantia in garantiasFiltradas" :key="garantia.folio" class="border-t hover:bg-gray-50">
              <td class="py-3 px-4">#{{ garantia.folio }}</td>
              <td class="py-3 px-4">{{ garantia.cliente }}</td>
              <td class="py-3 px-4">{{ garantia.producto }}</td>
              <td class="py-3 px-4">{{ formatDate(garantia.fecha_cierre) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        No hay garantías pendientes para este mes.
      </div>
    </div>
  </div>
</template>

<script>
import reportesNuevos from '@/Services/reportesNuevos.js';

export default {
  name: 'CalendarioGarantias',
  data() {
    const currentYear = new Date().getFullYear();
    return {
      anioSeleccionado: currentYear,
      anios: [currentYear - 1, currentYear, currentYear + 1],
      meses: [
        { nombre: 'Enero', value: 1 }, { nombre: 'Febrero', value: 2 }, { nombre: 'Marzo', value: 3 },
        { nombre: 'Abril', value: 4 }, { nombre: 'Mayo', value: 5 }, { nombre: 'Junio', value: 6 },
        { nombre: 'Julio', value: 7 }, { nombre: 'Agosto', value: 8 }, { nombre: 'Septiembre', value: 9 },
        { nombre: 'Octubre', value: 10 }, { nombre: 'Noviembre', value: 11 }, { nombre: 'Diciembre', value: 12 },
      ],
      garantias: [],
      mesSeleccionado: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    garantiasFiltradas() {
      if (!this.mesSeleccionado) return [];
      return this.garantias.filter(g => {
        const fecha = new Date(g.fecha_cierre);
        return fecha.getFullYear() === this.anioSeleccionado && 
               (fecha.getMonth() + 1) === this.mesSeleccionado;
      }).sort((a, b) => new Date(a.fecha_cierre) - new Date(b.fecha_cierre));
    },
  },
  watch: {
    anioSeleccionado() {
      this.mesSeleccionado = null;
      this.cargarDatos();
    },
  },
  methods: {
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      try {
        const fecha_inicio = `${this.anioSeleccionado}-01-01`;
        const fecha_fin = `${this.anioSeleccionado}-12-31`;
        const response = await reportesNuevos.CALENDARIO_GARANTIAS({ fecha_inicio, fecha_fin });
        if (response.data.errors) {
            this.error = Object.values(response.data.errors).join(', ');
            this.garantias = [];
            return;
        }
        this.garantias = response.data;
      } catch (error) {
        console.error('Error al cargar calendario:', error);
        this.error = 'No se pudo cargar el calendario. Intente de nuevo.';
      } finally {
        this.loading = false;
      }
    },
    getDiasMes(mes, anio) {
      const diasEnMes = new Date(anio, mes, 0).getDate();
      const primerDia = new Date(anio, mes - 1, 1).getDay();
      
      const diasConGarantias = {};
      this.garantias.forEach(g => {
        const fecha = new Date(g.fecha_cierre);
        if (fecha.getFullYear() === anio && (fecha.getMonth() + 1) === mes) {
          const dia = fecha.getDate();
          diasConGarantias[dia] = (diasConGarantias[dia] || 0) + 1;
        }
      });
      
      const dias = [];
      for (let i = 0; i < primerDia; i++) {
        dias.push({ dia: '', garantias: 0 });
      }
      for (let dia = 1; dia <= diasEnMes; dia++) {
        dias.push({
          dia,
          fecha: `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`,
          garantias: diasConGarantias[dia] || 0
        });
      }
      return dias;
    },
    getGarantiasPorMes(mes) {
      return this.garantias.filter(g => {
        const fecha = new Date(g.fecha_cierre);
        return fecha.getFullYear() === this.anioSeleccionado && (fecha.getMonth() + 1) === mes;
      }).length;
    },
    getNombreMes(mes) {
      return this.meses.find(m => m.value === mes)?.nombre || '';
    },
    verMes(mes) {
      this.mesSeleccionado = mes;
      this.$nextTick(() => {
        const element = document.querySelector('.bg-white.rounded-lg.shadow.p-6:last-child');
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      });
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('es-MX', options);
    },
  },
  mounted() {
    this.cargarDatos();
  }
};
</script>

<style scoped>
/* Estilos específicos para este componente */
</style>
